import React, { useEffect, useState } from "react";
import { useFileUrl } from "@/lib/fileAccess";
import * as avatarStore from "@/lib/avatarStore";

const DICEBEAR_BASE = "https://api.dicebear.com/7.x";

// DiceBear renders fully client-side: a public, auth-free, deterministic API
// (same seed -> same avatar). No backend proxy.
function dicebearUrl(style, seed) {
  return `${DICEBEAR_BASE}/${style || "notionists"}/svg?seed=${encodeURIComponent(seed)}`;
}

function cfgFromUser(user) {
  if (user && user.avatar_type) {
    return { avatar_type: user.avatar_type, avatar_url: user.avatar_url, dicebear_style: user.dicebear_style };
  }
  return null;
}

// The single source of truth for rendering a user's avatar everywhere.
export default function Avatar({ user, size = 32, className = "", decorative = false }) {
  const id = user?.id;
  const name = user?.name || "?";
  const color = user?.avatar_color || "rgb(var(--accent))";

  const [cfg, setCfg] = useState(() => cfgFromUser(user) || (id ? avatarStore.getCached(id) : null) || null);
  const [stage, setStage] = useState(0);   // which source in the fallback chain
  const [loaded, setLoaded] = useState(false);

  // Resolve the avatar config. The cache wins (WS keeps it fresh); fall back to
  // an inline config on the user object, else fetch.
  useEffect(() => {
    const cached = id ? avatarStore.getCached(id) : null;
    if (cached) { setCfg(cached); return; }
    const inline = cfgFromUser(user);
    if (inline) { avatarStore.prime(id, inline); setCfg(inline); return; }
    if (id) avatarStore.fetchAvatar(id).then((c) => { if (c) setCfg(c); });
  }, [id, user]);

  // Live updates from user_avatar_updated.
  useEffect(() => {
    if (!id) return;
    return avatarStore.subscribe(id, (c) => { setCfg(c); setStage(0); });
  }, [id]);

  // Reset the fallback chain whenever the config changes.
  useEffect(() => { setStage(0); setLoaded(false); }, [cfg]);

  // A stored avatar is a GridFS file id and needs a token; gravatar is an
  // external URL and needs none. The hook is called unconditionally, as hooks
  // must be, and given null when there is nothing to fetch.
  const storedFileId = cfg && cfg.avatar_url
    && (cfg.avatar_type === "upload" || cfg.avatar_type === "preset") ? cfg.avatar_url : null;
  const storedUrl = useFileUrl(storedFileId);

  // Build the source chain: [preset|upload|gravatar?, dicebear?].
  const sources = [];
  if (cfg && cfg.avatar_url && cfg.avatar_type === "gravatar") {
    sources.push(cfg.avatar_url);
  } else if (storedUrl) {
    sources.push(storedUrl);
  }
  if (id) sources.push(dicebearUrl(cfg?.dicebear_style, id));

  const dim = { width: size, height: size, minWidth: size, minHeight: size };

  // Initials are the base layer, not the last resort. The image sits on top and
  // only becomes visible once it has decoded, so a source that is slow, blocked
  // or rate-limited shows a readable avatar instead of an empty grey box — which
  // is what a list of them looked like, where a single one in the sidebar was
  // fast enough to hide the problem.
  return (
    <div
      className={`relative overflow-hidden flex-shrink-0 ${className}`}
      style={dim}
      title={decorative ? undefined : name}
      aria-hidden={decorative ? "true" : undefined}
      data-testid={id ? `avatar-${id}` : undefined}
    >
      <span
        className="absolute inset-0 flex items-center justify-center text-black font-bold select-none"
        style={{ background: color, fontSize: Math.round(size * 0.42) }}
      >
        {(name[0] || "?").toUpperCase()}
      </span>
      {stage < sources.length && (
        <img
          src={sources[stage]}
          alt={decorative ? "" : name}
          width={size}
          height={size}
          onLoad={() => setLoaded(true)}
          onError={() => { setLoaded(false); setStage((s) => s + 1); }}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity ${
            loaded ? "opacity-100" : "opacity-0"}`}
        />
      )}
    </div>
  );
}
